import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/Context';
import { fetchSubjectsByMaterial, getMaterialBySlug } from '../../services/materials.util';
import { checkMaterialPurchase } from '../../services/materialsPurchase.util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faLock, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import MetaComponent from '../../components/common/MetaComponent';

const RENEWAL_MESSAGE = 'Your subscription has ended. Please renew it to continue accessing this content.';

const MaterialPage = () => {
  const { materialCode } = useParams();
  const navigate = useNavigate();
  const { loggedIn } = useAuthContext();

  const [materialData, setMaterialData] = useState(null);
  const [materialLoading, setMaterialLoading] = useState(true);
  const [materialError, setMaterialError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  const [subjectsError, setSubjectsError] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState(null);

  useEffect(() => {
    if (!loggedIn) {
      navigate('/login', { replace: true });
    }
  }, [loggedIn, navigate]);

  useEffect(() => {
    if (!loggedIn) return;
    const load = async () => {
      try {
        setMaterialLoading(true);
        setMaterialError(null);
        const data = await getMaterialBySlug(materialCode);
        setMaterialData(data);
      } catch (err) {
        setMaterialError(err.message || 'Failed to load material.');
      } finally {
        setMaterialLoading(false);
      }
    };
    load();
  }, [materialCode, loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    const load = async () => {
      try {
        setSubjectsLoading(true);
        setSubjectsError(null);
        const data = await fetchSubjectsByMaterial(materialCode);
        setSubjects(data || []);
      } catch (err) {
        setSubjectsError(err.message || 'Failed to load subjects.');
      } finally {
        setSubjectsLoading(false);
      }
    };
    load();
  }, [materialCode, loggedIn]);

  useEffect(() => {
    if (!loggedIn || !materialData) return;
    const check = async () => {
      try {
        const response = await checkMaterialPurchase(materialData._id);
        setPurchaseStatus(response.data);
      } catch (err) {
        setPurchaseStatus({ hasPurchased: false });
      }
    };
    check();
  }, [loggedIn, materialData]);

  const loading = materialLoading || subjectsLoading;
  if (loading) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center text-white">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Loading material...</p>
          </div>
        </div>
      </div>
    );
  }

  if (materialError || subjectsError) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center text-white">
            <FontAwesomeIcon icon={faBookOpen} size="3x" className="mb-3 text-secondary" />
            <h5>Error Loading Data</h5>
            <p className="text-light mb-4">{materialError || subjectsError}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!materialData) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center text-white">
            <h5>Material Not Found</h5>
            <p className="text-light">The requested material could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  const hasAccess = purchaseStatus?.hasPurchased || materialData.isFree || !materialData.isPurchasable;
  const isSubscriptionExpired = purchaseStatus?.isExpired;
  const canExplore = (subject) => hasAccess;

  return (
    <div className="main-content">
      <MetaComponent
        title={`${materialData.title} || GVK Student Portal`}
        description={materialData.description || 'Study materials and PDFs.'}
      />
      <div className="container-fluid px-4 py-4">
        <div className="row mb-4">
          <div className="col-12">
            <h2 className="text-white mb-2">{materialData.title}</h2>
            {materialData.description && (
              <p className="text-light mb-0">{materialData.description}</p>
            )}
            {!hasAccess && materialData.isPurchasable && materialData.price > 0 && (
              <p className="text-white mt-2 mb-0">
                Price: ₹{materialData.discountPrice ?? materialData.price}
                {materialData.discountPrice && (
                  <span className="text-muted text-decoration-line-through ms-2">
                    ₹{materialData.price}
                  </span>
                )}
              </p>
            )}
            {isSubscriptionExpired && (
              <p className="text-warning mt-2 mb-0">
                {purchaseStatus.message || RENEWAL_MESSAGE}
              </p>
            )}
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <h4 className="text-white mb-2">Subjects</h4>
            <p className="text-light mb-0">
              {subjects.length} subject{subjects.length !== 1 ? 's' : ''} • PDFs available per subject
            </p>
          </div>
        </div>

        {subjects.length === 0 ? (
          <div className="text-center py-5 text-white">
            <FontAwesomeIcon icon={faBookOpen} size="3x" className="mb-3 opacity-50" />
            <h5>No Subjects Yet</h5>
            <p className="text-light">No subjects are linked to this material yet.</p>
          </div>
        ) : (
          <div className="row g-4">
            {subjects.map((subject, index) => (
              <div key={subject._id || index} className="col-lg-4 col-md-6">
                <div className="card h-100 bg-dark border border-secondary border-opacity-25 rounded-3 overflow-hidden">
                  <div className="card-body d-flex flex-column">
                    <h5 className="text-white mb-2">{subject.title}</h5>
                    {subject.description && (
                      <p className="text-light small mb-3 flex-grow-1">{subject.description}</p>
                    )}
                    <div className="d-flex justify-content-between small text-light mb-3">
                      <span>Materials: {subject.counts?.materails ?? 0}</span>
                    </div>
                    <div className="mt-auto">
                      {canExplore(subject) ? (
                        <button
                          className="btn btn-light w-100"
                          onClick={() =>
                            navigate(`/materials/${materialCode}/subject/${subject._id}`, {
                              state: { materialId: materialData._id }
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          Open Materials
                        </button>
                      ) : (
                        <button className="btn btn-secondary w-100" disabled>
                          <FontAwesomeIcon icon={faLock} className="me-2" />
                          {isSubscriptionExpired ? 'Renew Access' : 'Purchase to Access'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialPage;
